import React from "react";
import { useLocation } from "react-router-dom";

const scrollTo = (y: number) => {
  /**
   * JSDOM implements `window.scrollTo` so that it can
   * throw an error when it is called. This is a workaround to
   * prevent that error from being thrown.
   */
  try {
    window.scrollTo(0, y);
  } catch {
    /**
     * noop
     */
  }
};
/**
 * Keeps track of the current page scroll position so that
 * it can be restored if the user goes backward.
 */
export const ScrollMemory = React.memo(() => {
  const location = useLocation();

  const scrollMapRef = React.useRef<Map<string, number>>();

  if (!scrollMapRef.current) {
    scrollMapRef.current = new Map();
  }
  const scrollMap = scrollMapRef.current;

  React.useEffect(() => {
    /**
     * If `location.pathname` has changed, check whether or not the location (by key)
     * has been stored in `scrollMap`. If it has, then the value returned is the y-scroll
     * position. Call `window.scrollTo` to scroll to that height.
     *
     * If the page is not currently in the scrollMap, then it is a new _forward_ navigation.
     * (That is, if the key is present then it was stored on a previous page visit and you went back).
     */
    const key = location.key ?? "default";
    const prevYOffset = scrollMap.get(key) ?? 0;

    scrollTo(prevYOffset);

    /**
     * When the `location.pathname` changes, make sure to set the current scroll position
     * in the `scrollMap` so that it can be referred to in the case where we go back.
     */
    return () => {
      const yOffset = window.scrollY ?? document.documentElement?.scrollTop ?? 0;

      scrollMap.set(key, yOffset);
    };
  }, [location.pathname]);

  return null;
});
